import { useTranslation } from "@sagot/i18n";
import { Button, Icon, Popover, PopoverContent, PopoverTrigger } from "@sagot/ui";
import { useBuilderTools } from "./builder-tools";
import { NotesEditor } from "./notes-editor";

const N = "engage.workflows.extras.notes";

interface NoteChipProps {
  pathId: string;
  /** "Path C · Tuition". */
  pathTitle: string;
}

/**
 * The notes of a path on its header: "1 note" opens a small editor for them. Someone who can edit the workflow gets the
 * button even when the path has no note yet, to write the first one; a viewer sees it only when there is something to read.
 */
export function NoteChip({ pathId, pathTitle }: NoteChipProps) {
  const { t } = useTranslation();
  const tools = useBuilderTools();
  const count = tools?.notesAt(pathId).length ?? 0;
  if (!tools || (count === 0 && !tools.editable)) return null;
  const name = count > 0 ? t(`${N}.open`, { path: pathTitle }) : t(`${N}.addToPath`, { path: pathTitle });
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="ghost" size="sm" iconOnly={count === 0} aria-label={name} data-testid={`note-chip-${pathId}`}>
          <Icon name="sticky-note" />
          {count > 0 ? t(`${N}.count`, { count }) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80" aria-label={name}>
        <NotesEditor anchor={pathId} />
      </PopoverContent>
    </Popover>
  );
}
